<template>
	<AppBottomSheet 
		:active="active" 
		@toggling-popup="toggleStripeDashboardSheet" 
		:force-width="bottomSheetWidth">
		<ModalHeader :title="getString('stripe_dashboard')" />
		<div id="stripe-dashboard-container" ref="stripeDashboardContainer">
			<div v-if="loading" class="loading-container">
				<AppLoader />
			</div>
			<div v-show="!loading">
				<div id="stripe-notification-banner-container" class="stripe-component-container"></div>
				<div class="switch-buttons">
					<div class="tab-button"
						:class="{ active: activeView === 'payments' }"
						@click="activeView = 'payments'">{{ getString('payments') }}</div>
					<div class="tab-button"
						:class="{ active: activeView === 'payouts' }"
						@click="activeView = 'payouts'">{{ getString('payouts') }}</div>
					<div class="tab-button"
						:class="{ active: activeView === 'account' }"
						@click="activeView = 'account'">{{ getString('account') }}</div>
				</div>
				<div id="stripe-payments-list-container" class="stripe-component-container" :class="{ hidden: activeView !== 'payments' }"></div>
				<div id="stripe-payouts-list-container" class="stripe-component-container" :class="{ hidden: activeView !== 'payouts' }"></div>
				<div id="stripe-account-management-container" class="stripe-component-container" :class="{ hidden: activeView !== 'account' }"></div>
			</div>
		</div>
	</AppBottomSheet>
</template>

<script>
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import ModalHeader from "@/console-new/components/shared/ModalHeader.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import {useUserStore, useAdministrationStore} from "@/console-new/stores";
import {useLanguageStore} from "@/shared/stores";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import { useRouter, useRoute } from 'vue-router';

export default {
	name: "StripeDashboardCustom",
	components: {
		AppBottomSheet,
		ModalHeader,
		AppLoader
	},
	mixins: [WindowWidth],
	props: {
		active: {
			type: Boolean,
			default: false
		}
	},
	emits: ["update:active"],
	setup() {
		const userStore = useUserStore();
		const administrationStore = useAdministrationStore();
		const getString = useLanguageStore().getString;
		const router = useRouter();
		const route = useRoute();
		return {userStore, administrationStore, getString, router, route};
	},
	data() {
		return {
			loading: false,
			activeView: 'payments',
			notificationBannerComponent: null,
			paymentsListComponent: null,
			payoutsListComponent: null,
			accountManagementComponent: null,
		}
	},
	computed: {
		bottomSheetWidth() {
			if(this.windowWidth < 550) {
				return "100%";
			} else if(this.windowWidth < 768) {
				return "550px";
			} else if(this.windowWidth < 1080) {
				return "720px";
			} else {
				return "960px";
			}
		}
	},
	methods: {
		toggleStripeDashboardSheet(active) {
			this.$emit("update:active", active);
		},
		initializeStripeComponents() {
			this.loading = true;
			this.administrationStore.getStripeConnectInstance("dashboard", connectInstance => {
				if(connectInstance) {
					// Initialize the Stripe components
					this.$nextTick(() => {
						this.createStripeComponents(connectInstance);
					});
				} else {
					this.loading = false;
					this.$emit("update:active", false);
				}
			});
		},
		createStripeComponents(connectInstance) {
			// Create and mount notification banner
			this.notificationBannerComponent = connectInstance.create("notification-banner");
			const notificationBannerContainer = document.getElementById('stripe-notification-banner-container');
			if(notificationBannerContainer) {
				notificationBannerContainer.appendChild(this.notificationBannerComponent);

				this.notificationBannerComponent.setCollectionOptions({
				  fields: 'eventually_due',
				  futureRequirements: 'include',
				});
				// Set handler for when the component UI first appears
				this.notificationBannerComponent.setOnLoaderStart(() => {
					console.log("Notification banner component UI is now visible");
				});
				
				// Handle load errors
				this.notificationBannerComponent.setOnLoadError((loadError) => {
					console.error(`Notification banner component failed to load: ${loadError.error.type} - ${loadError.error.message}`);
				});
			}
			
			// Create and mount payments list
			this.paymentsListComponent = connectInstance.create("payments");
			const paymentsListContainer = document.getElementById('stripe-payments-list-container');
			if(paymentsListContainer) {
				paymentsListContainer.appendChild(this.paymentsListComponent);

				// Set handler for when the component UI first appears
				this.paymentsListComponent.setOnLoaderStart(() => {
					console.log("Payment list component UI is now visible");
					// Set a short timeout to wait for initial content to load
					setTimeout(() => {
						this.loading = false;
					}, 1000);
				});
				
				// Handle load errors
				this.paymentsListComponent.setOnLoadError((loadError) => {
					console.error(`Payment list component failed to load: ${loadError.error.type} - ${loadError.error.message}`);
					this.loading = false;
				});
				
			}
			
			// Create and mount payouts list
			this.payoutsListComponent = connectInstance.create("payouts");
			const payoutsListContainer = document.getElementById('stripe-payouts-list-container');
			if(payoutsListContainer) {
				payoutsListContainer.appendChild(this.payoutsListComponent);
				
				// Set handler for when the component UI first appears
				this.payoutsListComponent.setOnLoaderStart(() => {
					console.log("Payouts list component UI is now visible");
				});
				
				// Handle load errors
				this.payoutsListComponent.setOnLoadError((loadError) => {
					console.error(`Payouts list component failed to load: ${loadError.error.type} - ${loadError.error.message}`);
				});
			}

			// Create and mount account management
			this.accountManagementComponent = connectInstance.create("account-management");
			const accountManagementContainer = document.getElementById('stripe-account-management-container');
			if(accountManagementContainer) {
				accountManagementContainer.appendChild(this.accountManagementComponent);
				
				// Set handler for when the component UI first appears
				this.accountManagementComponent.setOnLoaderStart(() => {
					console.log("Account management component UI is now visible");
				});
				
				// Handle load errors
				this.accountManagementComponent.setOnLoadError((loadError) => {
					console.error(`Account management component failed to load: ${loadError.error.type} - ${loadError.error.message}`);
				});
			}

			//fallback loading
			setTimeout(() => {
				if(this.loading) {
					this.loading = false;
				}
			}, 8000);
		},
		cleanupStripeComponents() {

			// First destroy components before removing from DOM
			if (this.notificationBannerComponent) {
				try {
					// Call destroy() method if available, or unmount first, then remove
					if (typeof this.notificationBannerComponent.destroy === 'function') {
						this.notificationBannerComponent.destroy();
					} else if (typeof this.notificationBannerComponent.unmount === 'function') {
						this.notificationBannerComponent.unmount();
					}
				} catch (e) {
					console.error("Error destroying notification banner component:", e);
				}
			}

			if (this.paymentsListComponent) {
				try {
					if (typeof this.paymentsListComponent.destroy === 'function') {
						this.paymentsListComponent.destroy();
					} else if (typeof this.paymentsListComponent.unmount === 'function') {
						this.paymentsListComponent.unmount();
					}
				} catch (e) {
					console.error("Error destroying payments list component:", e);
				}
			}

			if (this.payoutsListComponent) {
				try {
					if (typeof this.payoutsListComponent.destroy === 'function') {
						this.payoutsListComponent.destroy();
					} else if (typeof this.payoutsListComponent.unmount === 'function') {
						this.payoutsListComponent.unmount();
					}
				} catch (e) {
					console.error("Error destroying payouts list component:", e);
				}
			}

			if (this.accountManagementComponent) {
				try {
					if (typeof this.accountManagementComponent.destroy === 'function') {
						this.accountManagementComponent.destroy();
					} else if (typeof this.accountManagementComponent.unmount === 'function') {
						this.accountManagementComponent.unmount();
					}
				} catch (e) {
					console.error("Error destroying account management component:", e);
				}
			}

			// Then remove from DOM for cleanliness
			const notificationBannerContainer = document.getElementById('stripe-notification-banner-container');
			if (notificationBannerContainer) {
				notificationBannerContainer.innerHTML = '';
			}
			
			const paymentsListContainer = document.getElementById('stripe-payments-list-container');
			if (paymentsListContainer) {
				paymentsListContainer.innerHTML = '';
			}
			
			const payoutsListContainer = document.getElementById('stripe-payouts-list-container');
			if (payoutsListContainer) {
				payoutsListContainer.innerHTML = '';
			}

			const accountManagementContainer = document.getElementById('stripe-account-management-container');
			if (accountManagementContainer) {
				accountManagementContainer.innerHTML = '';
			}

			// Reset component references
			this.notificationBannerComponent = null;
			this.paymentsListComponent = null;
			this.payoutsListComponent = null;
			this.accountManagementComponent = null;
		}
	},
	watch: {
		active(newValue) {
			if(newValue && this.userStore.currBusiness?.stripe_account) {
				this.initializeStripeComponents();
			} else if(!newValue) {
				this.cleanupStripeComponents();
				this.userStore.refreshBusiness(this.userStore.currBusinessId);
			}
		},
		'userStore.currBusiness': function(newValue) {
			if(newValue && newValue.stripe_account && this.active) {
				this.cleanupStripeComponents();
				this.initializeStripeComponents();
			}
		},
	},
}
</script>

<style scoped lang="scss">
#stripe-dashboard-container {
	min-height: 30vh;
	padding: 5px;
}

.loading-container {
	display: flex;
	justify-content: center;
	align-items: center;
	min-height: 30vh;
}

.stripe-component-container {
	padding-bottom: 20px;
	border-radius: 10px;
	overflow: hidden;

	&:last-child {
		margin-bottom: 0;
	}
	
	&.hidden {
		display: none;
	}
}


#stripe-notification-banner-container {
	margin-bottom: 0;
	padding-bottom: 20px;
}

.switch-buttons {
	display: flex;
	gap: 10px;
	margin-bottom: 15px;
	width: 100%;
	
	.tab-button {
		width: 33.33%; /* Changed from 50% to 33.33% for 3 buttons */
		padding: 10px;
		text-align: center;
		border-radius: 13px;
		cursor: pointer;
		border: 1px solid var(--color-primary);
		color: var(--color-primary);
		transition: all 0.3s ease;
		
		&.active {
			background-color: var(--color-active-button);
			color: var(--color-active-button-text);
		}
		
		&:hover:not(.active) {
			background-color: rgba(0, 0, 0, 0.05);
		}
	}

}
</style>